import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  StandardAgentSettings,
  UpsertStandardAgentSettingsBody,
} from "@forgepoint/shared";

interface RawStandardAgentSettings
  extends Omit<StandardAgentSettings, "createdAt" | "updatedAt"> {
  createdAt: string;
  updatedAt: string;
}

interface StandardAgentSettingsState {
  settings: StandardAgentSettings | null;
  isLoading: boolean;
  error: string | null;
  fetchSettings: () => Promise<void>;
  updateSettings: (params: UpsertStandardAgentSettingsBody) => Promise<boolean>;
}

export const useStandardAgentSettingsStore =
  create<StandardAgentSettingsState>()(
    immer((set) => ({
      settings: null,
      isLoading: false,
      error: null,

      fetchSettings: async () => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetchApi("standard-agent-settings", "GET");

          if (!response.ok) {
            throw new Error("Failed to fetch standard agent settings");
          }

          const rawSettings = (
            response.data as { settings: RawStandardAgentSettings | null }
          ).settings;

          if (!rawSettings) {
            set({ settings: null, isLoading: false });
            return;
          }

          const settings: StandardAgentSettings = {
            ...rawSettings,
            createdAt: new Date(rawSettings.createdAt),
            updatedAt: new Date(rawSettings.updatedAt),
          };

          set({ settings, isLoading: false });
        } catch (err) {
          const errorMsg =
            err instanceof Error ? err.message : "An unknown error occurred";
          captureException(err as Error);
          set({ error: errorMsg, isLoading: false });
        }
      },

      updateSettings: async (params) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetchApi("standard-agent-settings", "POST", {
            body: params,
          });

          if (!response.ok) {
            throw new Error(
              response.error ?? "Failed to update standard agent settings",
            );
          }

          const rawSettings = (
            response.data as { settings: RawStandardAgentSettings }
          ).settings;

          const settings: StandardAgentSettings = {
            ...rawSettings,
            createdAt: new Date(rawSettings.createdAt),
            updatedAt: new Date(rawSettings.updatedAt),
          };

          set({ settings, isLoading: false });
          return true;
        } catch (err) {
          const errorMsg =
            err instanceof Error ? err.message : "An unknown error occurred";
          captureException(err as Error);
          set({ error: errorMsg, isLoading: false });
          return false;
        }
      },
    })),
  );
